import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DocumentTypeMasterVm, DocumentRequestVm, DocumentVersionVm, Modules, Responsibility, PREDEFINED_DOCUMENT_TYPES, DocumentTypeDefinition } from 'shared/models';
import { AlertMessageService } from 'shared/core';
import { DocumentService } from 'shared/core';
import { AuthService } from 'shared/core';
import { Router } from '@angular/router';

@Component({
    selector: 'app-iatf-document-management',
    templateUrl: './IATFDocumentManagement.component.html',
    styleUrls: ['./IATFDocumentManagement.component.css'],
    standalone: false
})
/**
 * HOW TO ADD A NEW DOCUMENT TYPE TO THE SYSTEM:
 * 
 * 1. shared/models (Enums.ts):
 *    Add a meaningful constant to the `DocumentCode` enum.
 *    Example: `MachineStatementMfg = "MACHINE_STATEMENT_MFG"`
 * 
 * 2. shared/models (DocumentTypes.ts):
 *    Add a new entry to the `PREDEFINED_DOCUMENT_TYPES` array.
 *    The `code` must exactly match the enum value added in step 1.
 *    The `name` is what will appear in the Dropdown for this UI component.
 * 
 * 3. Use it in the target Component Print Logic:
 *    - Import `DocumentService`, `DocumentCode`, and `ActiveDocumentVersionVm`.
 *    - Inject `DocumentService` in the constructor.
 *    - Create a variable `ActiveDocumentVersion: ActiveDocumentVersionVm`.
 *    - Call `this.documentService.getActiveVersionByCode(DocumentCode.YourNewCode).subscribe(res => this.ActiveDocumentVersion = res);`
 *    - In your Print HTML template, render the `FormatNo` property:
 *      `${this.ActiveDocumentVersion?.FormatNo ? 'Format No: ' + this.ActiveDocumentVersion.FormatNo : ''}`
 */
export class IATFDocumentManagementComponent implements OnInit {
    validateForm!: UntypedFormGroup;
    DocumentList: DocumentTypeMasterVm[] = [];
    NewDocument: DocumentTypeMasterVm = new DocumentTypeMasterVm();
    isVisible = false;
    isUpdate = false;
    isLoading = false;
    isTableLoading = false;
    PopUpTitle = "Add New Document Type";
    searchValue = '';
    validateVersionForm!: UntypedFormGroup;
    isVersionModalVisible = false;
    selectedDocumentTypeId: number = 0;
    documentTypeOptions: DocumentTypeDefinition[] = PREDEFINED_DOCUMENT_TYPES;
    Departments: any[] = [];
    permission = {
        View: false,
        Add: false,
        Edit: false,
        Delete: false,
    };

    // Timeline
    timelineData: import('shared/models').DocumentTimelineVm[] = [];
    isTimelineVisible = false;
    isTimelineLoading = false;

    constructor(
        private fb: UntypedFormBuilder,
        private documentService: DocumentService,
        private alertService: AlertMessageService,
        private modal: NzModalService,
        private auth: AuthService,
        private router: Router
    ) { }

    ngOnInit() {
        this.permission.View = this.auth.CheckResponsibility(Modules.ComplianceDocumentManagement, Responsibility.View);
        this.permission.Add = this.auth.CheckResponsibility(Modules.ComplianceDocumentManagement, Responsibility.Add);
        this.permission.Edit = this.auth.CheckResponsibility(Modules.ComplianceDocumentManagement, Responsibility.Edit);
        this.permission.Delete = this.auth.CheckResponsibility(Modules.ComplianceDocumentManagement, Responsibility.Delete);
        if (!this.permission.View) {
            this.router.navigate(['/home/unauthorized']);
        }
        this.validateForm = this.fb.group({
            DocumentName: [null, [Validators.required, Validators.maxLength(100)]],
            FormatNo: [null, [Validators.required, Validators.maxLength(50)]],
            Description: [null, [Validators.maxLength(500)]]
        });

        this.validateVersionForm = this.fb.group({
            DocumentVersionId: [0],
            DocumentTypeId: [null, [Validators.required]],
            DocumentName: [null],
            FormatNo: [null],
            DepartmentId: [null, [Validators.required]],
            VersionNo: [null, [Validators.maxLength(20)]],
            EffectiveDate: [null, [Validators.required]],
            ReviewDate: [null, [Validators.required]],
            IsActive: [true],
            Remark: ['']
        });

        this.validateForm.addControl('IsActive', this.fb.control(true));

        this.GetAllDocuments();
        this.GetDepartments();
    }

    get f() { return this.validateForm.controls; }

    GetDepartments() {
        this.documentService.getDepartments().subscribe(res => {
            this.Departments = res;
        });
    }

    GetAllDocuments() {
        this.isTableLoading = true;
        const filter = new DocumentRequestVm();
        filter.IsActive = true;

        if (this.searchValue) {
            filter.SearchTerm = this.searchValue;
        }

        // Use the new combined endpoint that returns document types with versions
        this.documentService.getDocumentTypesWithVersions(filter).subscribe({
            next: (res) => {
                this.DocumentList = res;
                // Initialize expand records
                this.initializeExpandRecords(this.DocumentList);
                this.isTableLoading = false;
            },
            error: (err) => {
                this.isTableLoading = false;
                console.error(err);
                this.alertService.error("Failed to load documents");
            }
        });
    }

    private initializeExpandRecords(documentList: DocumentTypeMasterVm[]): void {
        documentList.forEach((doc) => {
            doc.ExpandRecord = false;
            // Versions are already loaded from the API
        });
    }

    onExpandChange(data: DocumentTypeMasterVm, checked: boolean): void {
        // No need to lazy load, versions are already included from the API
    }

    Search() {
        this.GetAllDocuments();
    }

    Reset() {
        this.searchValue = '';
        this.GetAllDocuments();
    }

    selectedDocumentDescription: string = '';

    onDocumentTypeChange(name: string): void {
        const docType = this.documentTypeOptions.find(x => x.name === name);
        this.selectedDocumentDescription = docType ? (docType.description || '') : '';
    }

    showModal(): void {
        this.PopUpTitle = "Add New Document Type";
        this.validateForm.reset();
        this.validateForm.patchValue({ IsActive: true });
        this.NewDocument = new DocumentTypeMasterVm();
        this.selectedDocumentDescription = '';

        // Re-enable DocumentName for new documents
        this.validateForm.get('DocumentName')?.enable();

        this.isVisible = true;
    }

    OpenEditPop(data: DocumentTypeMasterVm) {
        this.PopUpTitle = "Modify Document Type";
        this.NewDocument = { ...data }; // Clone
        this.validateForm.patchValue({
            DocumentName: data.DocumentName,
            FormatNo: data.FormatNo,
            Description: data.Description,
            IsActive: data.IsActive
        });

        // Set description for existing document
        this.onDocumentTypeChange(data.DocumentName);

        // Disable DocumentName when editing to prevent code changes
        this.validateForm.get('DocumentName')?.disable();

        this.isVisible = true;
    }

    handleOk(): void {
        if (this.validateForm.invalid) {
            Object.values(this.validateForm.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            return;
        }

        this.isLoading = true;
        const formValues = this.validateForm.value;
        this.NewDocument.DocumentName = formValues.DocumentName;
        this.NewDocument.DocumentCode = this.documentTypeOptions.find(x => x.name === formValues.DocumentName)?.code ?? '';
        this.NewDocument.FormatNo = formValues.FormatNo;
        this.NewDocument.Description = formValues.Description;
        this.NewDocument.IsActive = formValues.IsActive;

        this.documentService.saveDocumentType(this.NewDocument).subscribe({
            next: (res) => {
                this.alertService.success("Document Type Saved Successfully");
                this.isLoading = false;
                this.isVisible = false;
                this.GetAllDocuments();
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.error(err.error || "An error occurred");
            }
        });
    }

    handleCancel(): void {
        this.isVisible = false;
    }

    handleDelete(data: DocumentTypeMasterVm) {
        this.modal.confirm({
            nzTitle: 'Confirm',
            nzContent: 'Are you sure to delete this Document Type?',
            nzOkDanger: true,
            nzOnOk: () => {
                this.documentService.deleteDocumentType(data).subscribe({
                    next: (res) => {
                        this.alertService.success("Document Type Deleted Successfully");
                        this.GetAllDocuments();
                    },
                    error: (err) => {
                        this.alertService.error(err.error || "An error occurred");
                    }
                });
            }
        });
    }

    navigateToDocumentFormatVersioning() {
        this.router.navigate(['home/document/versionmanagement']);
    }

    openAddVersionModal(documentType: DocumentTypeMasterVm): void {
        // Set up form for adding new version
        this.isUpdate = false;
        this.selectedDocumentTypeId = documentType.DocumentTypeId;
        this.validateVersionForm.reset();
        this.validateVersionForm.patchValue({
            DocumentTypeId: documentType.DocumentTypeId,
            DocumentName: documentType.DocumentName,
            FormatNo: documentType.FormatNo,
            IsActive: true,
            Remark: ''
        });
        this.isVersionModalVisible = true;
    }

    openEditVersionModal(version: DocumentVersionVm): void {
        // Set up form for editing existing version
        this.isUpdate = true;
        this.selectedDocumentTypeId = version.DocumentTypeId;
        this.isVersionModalVisible = true;

        // Load full version data and populate form
        this.documentService.getDocumentVersionById(version.DocumentVersionId).subscribe({
            next: (res) => {
                this.validateVersionForm.patchValue({
                    DocumentVersionId: res.DocumentVersionId,
                    DocumentTypeId: res.DocumentTypeId,
                    DocumentName: res.DocumentName,
                    FormatNo: res.FormatNo,
                    DepartmentId: res.DepartmentId,
                    VersionNo: res.VersionNo,
                    EffectiveDate: res.EffectiveDate ? new Date(res.EffectiveDate) : null,
                    ReviewDate: res.ReviewDate ? new Date(res.ReviewDate) : null,
                    IsActive: res.IsActive,
                    Remark: res.Remark
                });
            },
            error: (err) => {
                console.error(err);
                this.alertService.error("Failed to load version details");
            }
        });
    }

    handleDeleteVersion(version: DocumentVersionVm): void {
        this.modal.confirm({
            nzTitle: 'Are you sure you want to delete this Document Version?',
            nzContent: `<b>Version ${version.VersionNo}</b> of ${version.DocumentName} will be permanently deleted.`,
            nzOkText: 'Delete',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzOnOk: () => {
                this.documentService.deleteDocumentVersion(version).subscribe({
                    next: (res) => {
                        this.alertService.success("Document Version deleted successfully");
                        this.GetAllDocuments();
                    },
                    error: (err) => {
                        console.error(err);
                        this.alertService.error("Failed to delete document version");
                    }
                });
            },
            nzCancelText: 'Cancel'
        });
    }

    handleVersionCancel(): void {
        this.isVersionModalVisible = false;
    }

    handleVersionOk(): void {
        if (this.validateVersionForm.invalid) {
            Object.values(this.validateVersionForm.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            return;
        }

        this.isLoading = true;
        const formValues = this.validateVersionForm.value;
        const versionData = new DocumentVersionVm();

        if (this.isUpdate) {
            versionData.DocumentVersionId = formValues.DocumentVersionId;
        }

        versionData.DocumentTypeId = formValues.DocumentTypeId;
        versionData.DepartmentId = formValues.DepartmentId;
        versionData.VersionNo = formValues.VersionNo;
        versionData.EffectiveDate = formValues.EffectiveDate;
        versionData.ReviewDate = formValues.ReviewDate;
        versionData.IsActive = formValues.IsActive;
        versionData.Remark = formValues.Remark;

        // We need to pass helper properties if API needs them or just IDs
        // The API usually takes the VM. 

        this.documentService.saveDocumentVersion(versionData).subscribe({
            next: (res) => {
                this.alertService.success(this.isUpdate ? "Version updated successfully" : "Version added successfully");
                this.isLoading = false;
                this.isVersionModalVisible = false;
                this.GetAllDocuments();
            },
            error: (err) => {
                this.isLoading = false;
                console.error(err);
                this.alertService.error(err.error || "An error occurred while saving version");
            }
        });
    }

    openTimeline(data: DocumentTypeMasterVm) {
        this.isTimelineVisible = true;
        this.isTimelineLoading = true;
        this.documentService.getDocumentTimeline(data.DocumentTypeId).subscribe(res => {
            this.timelineData = res;
            this.isTimelineLoading = false;
        }, err => {
            this.isTimelineLoading = false;
            this.alertService.error("Failed to load timeline");
        });
    }

    closeTimeline() {
        this.isTimelineVisible = false;
        this.timelineData = [];
    }
}
